/* ============================================================
   Revam Predictive Search (GTG-013) — custom client-side engine.
   <revam-predictive-search> wraps a search input + a [data-predictive-search]
   results container. Instead of Shopify's Predictive Search API, this builds
   a product index from /products.json once per session, then filters it
   in-memory on every keystroke. Matching covers product title, vendor
   (brand), and variant SKU (brand SKU) using token-prefix + substring
   semantics, so SKU fragments such as HZ, BT, BM2000, MA600EXT surface
   the matching products.

   The rendered results reproduce the exact DOM contract emitted by
   sections/revam-predictive-search.liquid (same IDs + class names), so
   revam-predictive-search.css styles them unchanged. The modal markup,
   form, input, overlay, keyboard navigation, aria/combobox attributes,
   caching, abort, and mobile-overlay behavior are all preserved.

   Requires revam-search-form.js (RevamSearchForm base) loaded first.
   ============================================================ */
(function () {
  if (!window.RevamSearchForm || customElements.get('revam-predictive-search')) return;

  /* ---------- Index loading + caching (shared across all instances) ---------- */
  var INDEX_CACHE_KEY = 'gtg:search-index:v1';
  var INDEX_CACHE_TTL = 60 * 60 * 1000; // 1 hour
  var PRODUCTS_LIMIT = 10;

  var sharedIndex = null;       // array of compact product records
  var indexPromise = null;      // in-flight load (deduped across instances)

  function readCachedIndex() {
    try {
      var raw = sessionStorage.getItem(INDEX_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.ts || !Array.isArray(parsed.products)) return null;
      if (Date.now() - parsed.ts > INDEX_CACHE_TTL) return null;
      return parsed.products;
    } catch (e) { return null; }
  }

  function writeCachedIndex(products) {
    try { sessionStorage.setItem(INDEX_CACHE_KEY, JSON.stringify({ ts: Date.now(), products: products })); }
    catch (e) { /* quota / private mode — ignore, in-memory cache still works */ }
  }

  // Minimal lowercase token list used for prefix matching; hyphens, spaces,
  // slashes and commas split tokens (this is what makes "HZ" match "AGRK-18V-HZ-BT").
  function tokenize(str) {
    if (!str) return [];
    return String(str).toLowerCase().split(/[\s\-\/_,]+/).filter(Boolean);
  }

  // Compact a raw /products.json product into a small search record.
  function compactProduct(p) {
    var firstImage = (p.images && p.images.length) ? p.images[0].src : null;
    var secondImage = (p.images && p.images.length > 1) ? p.images[1].src : null;
    var variants = p.variants || [];
    var firstAvail = variants.length ? variants[0] : null;
    // Collect every variant SKU (lowercased) + their tokens for matching.
    var skus = [];
    var skuTokens = [];
    for (var i = 0; i < variants.length; i++) {
      var sku = variants[i].sku;
      if (sku) {
        var skuLow = String(sku).toLowerCase();
        skus.push(skuLow);
        var toks = tokenize(sku);
        for (var j = 0; j < toks.length; j++) if (skuTokens.indexOf(toks[j]) === -1) skuTokens.push(toks[j]);
      }
    }
    return {
      t: p.title || '',                          // title
      v: (p.vendor || ''),                        // vendor / brand
      ty: (p.product_type || ''),                 // product type (matched too)
      tg: tokenize(p.tags || '').join(' '),       // tags (matched too)
      h: p.handle,                                // url handle
      sk: skus,                                   // variant SKUs (lowercased)
      skt: skuTokens,                             // SKU tokens (for prefix match)
      p: firstAvail ? firstAvail.price : null,    // display price
      cap: firstAvail ? firstAvail.compare_at_price : null,
      av: firstAvail ? firstAvail.available : true,
      img: firstImage,
      img2: secondImage
    };
  }

  // Fetch /products.json pages until exhausted, build the index, cache it.
  function loadIndex(signal) {
    if (sharedIndex) return Promise.resolve(sharedIndex);
    if (indexPromise) return indexPromise;

    var cached = readCachedIndex();
    if (cached && cached.length) {
      sharedIndex = cached;
      return Promise.resolve(sharedIndex);
    }

    indexPromise = (function () {
      var collected = [];
      var page = 1;
      function next() {
        var url = '/products.json?limit=250&page=' + page;
        return fetch(url, { signal: signal })
          .then(function (r) { if (!r.ok) throw new Error('products.json ' + r.status); return r.json(); })
          .then(function (data) {
            var prods = (data && data.products) || [];
            for (var i = 0; i < prods.length; i++) collected.push(compactProduct(prods[i]));
            if (prods.length === 250) { page++; return next(); }
            return collected;
          });
      }
      return next().then(function (products) {
        sharedIndex = products;
        writeCachedIndex(products);
        indexPromise = null;
        return products;
      }).catch(function (err) {
        indexPromise = null; // allow a retry on the next attempt
        throw err;
      });
    })();
    return indexPromise;
  }

  /* ---------- Matching ---------- */
  // Returns a score (>0) if the product matches the query, else 0.
  // Query is already lowercased + trimmed. Higher score ranks first.
  //   token-prefix hit on SKU  -> 100  (strongest — exact SKU intent)
  //   token-prefix hit on title/vendor/type/tag -> 60
  //   whole-query substring in SKU -> 40
  //   whole-query substring in title/vendor/type/tag -> 20
  //
  // "Token-prefix" is unidirectional: a field token must START WITH a query
  // token. The whole-query substring fallback (below) already covers the
  // reverse case (e.g. "r32" is itself an exact token of SKU "p1-r32", and
  // would also be caught as a substring of the full SKU). Keeping the prefix
  // direction one-way avoids false positives where a tiny field token (e.g.
  // "b") matches a longer query (e.g. "bt").
  function scoreProduct(rec, queryLow) {
    if (!queryLow) return 0;
    var qTokens = queryLow.split(/[\s]+/).filter(Boolean);

    var i, j;
    for (i = 0; i < qTokens.length; i++) {
      var qt = qTokens[i];
      // SKU tokens — strongest signal
      for (j = 0; j < rec.skt.length; j++) { if (rec.skt[j].indexOf(qt) === 0) return 100; }
      // title tokens
      var tTokens = tokenize(rec.t);
      for (j = 0; j < tTokens.length; j++) { if (tTokens[j].indexOf(qt) === 0) return 60; }
      // vendor tokens
      var vTokens = tokenize(rec.v);
      for (j = 0; j < vTokens.length; j++) { if (vTokens[j].indexOf(qt) === 0) return 60; }
      // type / tag tokens
      var tyTokens = tokenize(rec.ty);
      for (j = 0; j < tyTokens.length; j++) { if (tyTokens[j].indexOf(qt) === 0) return 60; }
      var tgTokens = tokenize(rec.tg);
      for (j = 0; j < tgTokens.length; j++) { if (tgTokens[j].indexOf(qt) === 0) return 60; }
    }

    // Whole-query substring fallback (broadest recall): covers a fragment that
    // is not itself a token prefix — e.g. "r32" inside the full SKU "p1-r32".
    for (i = 0; i < rec.sk.length; i++) { if (rec.sk[i].indexOf(queryLow) !== -1) return 40; }
    if (rec.t.toLowerCase().indexOf(queryLow) !== -1) return 20;
    if (rec.v.toLowerCase().indexOf(queryLow) !== -1) return 20;
    if (rec.ty.toLowerCase().indexOf(queryLow) !== -1) return 20;
    if (rec.tg.indexOf(queryLow) !== -1) return 20;
    return 0;
  }

  function searchProducts(query) {
    if (!sharedIndex || !sharedIndex.length) return [];
    var queryLow = query.trim().toLowerCase();
    if (!queryLow) return [];
    var scored = [];
    for (var i = 0; i < sharedIndex.length; i++) {
      var s = scoreProduct(sharedIndex[i], queryLow);
      if (s > 0) scored.push({ rec: sharedIndex[i], score: s });
    }
    scored.sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      // tie-break alphabetically by title for stable ordering
      return a.rec.t.localeCompare(b.rec.t, undefined, { sensitivity: 'base' });
    });
    var out = [];
    for (var k = 0; k < scored.length && out.length < PRODUCTS_LIMIT; k++) out.push(scored[k].rec);
    return out;
  }

  /* ---------- Rendering (reproduces revam-predictive-search.liquid DOM) ---------- */
  function moneyString(priceStr) {
    // /products.json returns price as a string like "250.00". Render as
    // "$X,XXX.XX USD" — matches the theme's money_with_currency output pattern.
    if (priceStr == null || priceStr === '') return '';
    var num = Number(priceStr);
    if (isNaN(num)) return '$' + priceStr;
    return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' USD';
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function productUrl(handle) { return '/products/' + handle; }
  function thumb(src) {
    // Shopify image URL: append a width param for a small thumbnail.
    if (!src) return '';
    return src.indexOf('?') !== -1 ? src + '&width=400' : src + '?width=400';
  }

  function renderProducts(products, query) {
    var html = '';
    html += '<div id="predictive-search-results" role="listbox">';
    if (products.length > 0) {
      // Products-only this run: no suggestions sidebar -> use the --no-suggestions
      // modifier so the products column goes full-width (per CSS).
      html += '<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">';
      html += '<div class="predictive-search__result-group predictive-search__result-group--products">';
      html += '<h2 id="predictive-search-products" class="predictive-search__heading">Products</h2>';
      html += '<ul id="predictive-search-results-products-list" class="predictive-search__results-list" role="group" aria-labelledby="predictive-search-products">';
      for (var i = 0; i < products.length; i++) {
        var p = products[i];
        var onSale = p.cap && p.cap !== '' && Number(p.cap) > Number(p.p);
        html += '<li id="predictive-search-option-product-' + (i + 1) + '" class="predictive-search__list-item" role="option" aria-selected="false">';
        html += '<a href="' + productUrl(p.h) + '" class="predictive-search__item predictive-search__item--link-with-thumbnail" tabindex="-1">';
        if (p.img) {
          html += '<div class="predictive-search__image-wrapper">';
          html += '<img class="predictive-search__image predictive-search__image--primary" src="' + escapeHtml(thumb(p.img)) + '" alt="' + escapeHtml(p.t) + '" width="200" height="200" loading="lazy">';
          if (p.img2) {
            html += '<img class="predictive-search__image predictive-search__image--secondary" src="' + escapeHtml(thumb(p.img2)) + '" alt="' + escapeHtml(p.t) + '" width="200" height="200" loading="lazy">';
          }
          html += '</div>';
        } else {
          html += '<div class="predictive-search__image-wrapper predictive-search__image-wrapper--placeholder"></div>';
        }
        html += '<div class="predictive-search__item-content">';
        html += '<p class="predictive-search__item-heading">' + escapeHtml(p.t) + '</p>';
        html += '<div class="predictive-search__item-price">';
        if (onSale) {
          html += '<span class="predictive-search__price predictive-search__price--sale">' + escapeHtml(moneyString(p.p)) + '</span>';
          html += '<s class="predictive-search__price predictive-search__price--was">' + escapeHtml(moneyString(p.cap)) + '</s>';
        } else {
          html += '<span class="predictive-search__price">' + escapeHtml(moneyString(p.p)) + '</span>';
        }
        html += '</div>'; // price
        html += '</div>'; // content
        html += '</a>';
        html += '</li>';
      }
      html += '</ul>';
      html += '</div>'; // result-group
      html += '</div>'; // groups-wrapper
    } else {
      // CSS intentionally hides .predictive-search__no-results (display:none !important),
      // so only the "Search for ..." footer below ever shows for an empty result set.
      html += '<div class="predictive-search__no-results">No results for &ldquo;' + escapeHtml(query) + '&rdquo;</div>';
    }

    // "Search for <query>" footer — submits the form. Reproduced verbatim from
    // the Liquid so styling + the JS form-submit hook keep working.
    html += '<div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">';
    html += '<button class="predictive-search__item predictive-search__item--term" tabindex="-1" role="option" aria-selected="false" onclick="this.closest(\'revam-predictive-search\')?.querySelector(\'form\')?.submit()">';
    html += '<span data-predictive-search-search-for-text>Search for &ldquo;' + escapeHtml(query) + '&rdquo;</span>';
    html += '<svg class="predictive-search__arrow" width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path fill-rule="evenodd" clip-rule="evenodd" d="M9.53 3.97a.75.75 0 0 1 1.06 0l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H3a.75.75 0 0 1 0-1.5h9.25L9.53 5.03a.75.75 0 0 1 0-1.06Z" fill="currentColor"/></svg>';
    html += '</button>';
    html += '</div>';
    html += '</div>'; // #predictive-search-results
    return html;
  }

  /* ---------- Custom element ---------- */
  class RevamPredictiveSearch extends window.RevamSearchForm {
    constructor() {
      super();
      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allInstances = document.querySelectorAll('revam-predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';
      this.statusElement = this.querySelector('.predictive-search-status');
      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    getQuery() { return this.input.value.trim(); }

    onChange() {
      super.onChange();
      var newSearchTerm = this.getQuery();
      if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
        this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
      }
      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) { this.showDefaultSuggestions(); return; }
      this.hideDefaultSuggestions();
      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (super.shouldResetForm()) {
        this.searchTerm = '';
        this.abortController.abort();
        this.abortController = new AbortController();
        this.closeResults(true);
      }
    }

    onFocus() {
      var currentSearchTerm = this.getQuery();
      if (!currentSearchTerm.length) { this.showDefaultSuggestions(); return; }
      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      // Don't auto-close inside the mobile overlay (it has explicit close controls).
      if (this.closest('.revam-search-overlay')) return;
      setTimeout(() => { if (!this.contains(document.activeElement)) this.close(); });
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.showDefaultSuggestions();
      switch (event.code) {
        case 'ArrowUp': event.preventDefault(); this.switchOption('up'); break;
        case 'ArrowDown': event.preventDefault(); this.switchOption('down'); break;
        case 'Enter': this.selectOption(); break;
        case 'Escape': this.close(); this.input.blur(); break;
      }
    }

    onKeydown(event) {
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
    }

    updateSearchForTerm(previousTerm, newTerm) {
      var el = this.querySelector('[data-predictive-search-search-for-text]');
      var text = el ? el.innerText : null;
      if (text) {
        if (previousTerm && (text.match(new RegExp(previousTerm, 'g'))?.length || 0) > 1) return;
        el.innerText = previousTerm ? text.replace(previousTerm, newTerm) : text;
      }
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;
      var moveUp = direction === 'up';
      var selectedElement = this.querySelector('[aria-selected="true"]');
      var allVisibleElements = Array.from(
        this.querySelectorAll('li, button.predictive-search__item')
      ).filter(function (el) { return el.offsetParent !== null; });
      if (moveUp && !selectedElement) return;

      var selectedIndex = allVisibleElements.indexOf(selectedElement);
      var activeIndex = 0;
      if (!moveUp && selectedElement) {
        activeIndex = selectedIndex === allVisibleElements.length - 1 ? 0 : selectedIndex + 1;
      } else if (moveUp) {
        activeIndex = selectedIndex <= 0 ? allVisibleElements.length - 1 : selectedIndex - 1;
      }
      if (activeIndex === selectedIndex) return;

      var activeElement = allVisibleElements[activeIndex];
      activeElement.setAttribute('aria-selected', true);
      if (selectedElement) selectedElement.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      var selected = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
      if (selected) selected.click();
    }

    getSearchResults(searchTerm) {
      var queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLoading();

      // Per-query result cache (within this page load): synchronous render.
      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      var self = this;
      // Ensure the product index is loaded (once per session), then filter.
      loadIndex(this.abortController.signal)
        .then(function () {
          var markup = renderProducts(searchProducts(searchTerm), searchTerm);
          // Share the rendered markup across every modal instance + cache it.
          self.allInstances.forEach(function (inst) { inst.cachedResults[queryKey] = markup; });
          self.renderSearchResults(markup);
        })
        .catch(function (error) {
          // code 20 == AbortError (fetch cancelled by reset/re-entry) — ignore.
          if (error && error.code === 20) return;
          self.close();
          if (window.console) console.warn('[revam-predictive-search] index load failed', error);
        });
    }

    setLoading() { this.setAttribute('loading', true); }

    renderSearchResults(resultsMarkup) {
      this.hideDefaultSuggestions();
      var inputId = this.input.getAttribute('id');
      var defaults = document.getElementById('default-suggestions-' + inputId);
      var defaultsClone = defaults ? defaults.cloneNode(true) : null;

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      if (defaultsClone) {
        this.predictiveSearchResults.insertBefore(defaultsClone, this.predictiveSearchResults.firstChild);
      }
      this.setAttribute('results', true);
      this.removeAttribute('loading');
      this.open();
    }

    getResultsMaxHeight() {
      var rect = this.input.getBoundingClientRect();
      this.resultsMaxHeight = window.innerHeight - rect.bottom - 24;
      return Math.max(240, this.resultsMaxHeight);
    }

    open() {
      if (!this.closest('.revam-search-overlay')) {
        this.predictiveSearchResults.style.maxHeight = (this.resultsMaxHeight || this.getResultsMaxHeight()) + 'px';
      }
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
      if (!this.getQuery().length) this.showDefaultSuggestions();
    }

    showDefaultSuggestions() {
      var inputId = this.input.getAttribute('id');
      var defaults = document.getElementById('default-suggestions-' + inputId);
      var wrapper = this.querySelector('#predictive-search-results-groups-wrapper');
      if (wrapper) wrapper.style.display = 'none';
      var results = this.querySelector('#predictive-search-results');
      if (results) results.style.display = 'none';
      this.removeAttribute('results');
      this.removeAttribute('loading');
      if (defaults) defaults.classList.remove('hidden');
      if (!this.getAttribute('open')) this.open();
    }

    hideDefaultSuggestions() {
      var inputId = this.input.getAttribute('id');
      var defaults = document.getElementById('default-suggestions-' + inputId);
      if (defaults) defaults.classList.add('hidden');
      var wrapper = this.querySelector('#predictive-search-results-groups-wrapper');
      if (wrapper) wrapper.style.display = '';
      var results = this.querySelector('#predictive-search-results');
      if (results) results.style.display = '';
    }

    close(clearSearchTerm) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }
      var selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', false);
      this.input.setAttribute('aria-activedescendant', '');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', false);
      this.resultsMaxHeight = false;
      this.predictiveSearchResults.removeAttribute('style');
    }
  }

  customElements.define('revam-predictive-search', RevamPredictiveSearch);
})();
